import { useRef } from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import { useIsMutating } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Button, InputField, Panel, BUTTON_THEMES, Form } from 'ht-styleguide';

/* Utils */
// import {setZipDataCookie} from '@utils/cookies/zipCodeTestCookie';
import { SegmentTrackingAction } from '@integrations/segment/sku';

/* Constants */
import { zipCodeCta } from '@components/Sku/ZipCodeEntry/zipcodeentry.constants';
import { addSkuPath } from '@constants/paths';
import { YupSchemaResolver } from '@constants/validations';

/* Styles */
import styles from '../locationPage.module.scss';

/* Inline Types */
type ServiceZipCodeEntryProps = {
  onGetPriceClick: BaseAnyFunction;
  className?: string;
  skuId: number;
  serviceName: string;
  cityName: string;
};
type YupFormProps = { zipCode: string; email: string };

/**
 * ServiceZipCodeEntry
 *
 * @param {number | undefined} skuId
 * @param {(...any: any[]) => any} onGetPriceClick
 * @param {string | undefined} className
 * @param {string} serviceName
 * @param {string} cityName
 * @returns {JSX.Element}
 * @constructor
 */
const ServiceZipCodeEntry = ({ skuId, onGetPriceClick, className, serviceName, cityName }: ServiceZipCodeEntryProps) => {
  // This would normally be used globally, but folding out for a specific usage here
  const fetching = useIsMutating();
  const router = useRouter();
  const prefetchedTriggered = useRef(false);

  /*
   * Lets preload the add-sku page once a user starts interacting with form.
   * We don't do it onload, because it impacts initial loading.
   */
  const prefetchNextPage = () => {
    if (!prefetchedTriggered.current) {
      prefetchedTriggered.current = true;
      /* TODO: Uncomment this when we allow for add-sku IN next app */
      router.prefetch(addSkuPath(`${skuId}`));
    }
  };

  const actionOnSubmit: SubmitHandler<YupFormProps> = ({ zipCode, email = '' }) => {
    onGetPriceClick();
    /* Send data to segment */
    SegmentTrackingAction.getAPriceClickSegment({ serviceName, skuId, zipCode, email, treatment: 'testB' });
    // we don't want to override since we are not "realy" zipgating here
    // setZipDataCookie({zipCode});
  };

  /* Hooks */
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<YupFormProps>({
    ...YupSchemaResolver(['zipCode'], 'onChange', {
      zipCode: '',
      email: '',
    }),
  });

  return (
    <Panel largeBorderRadius className={cn(className, styles.zipEmail, 'paddingX-medium paddingY-medium')} key={router.asPath}>
      <Form classes={styles.field_sales__form} onSubmit={handleSubmit(actionOnSubmit)}>
        <h4 className="htBlue700 paddingBottom-medium">
          Book your {cityName} {serviceName}
        </h4>
        <InputField
          onFocus={prefetchNextPage}
          label="Zip Code"
          maxLength={5}
          type="text"
          containerClass="paddingBottom-small2"
          error={errors.zipCode?.message}
          {...register('zipCode')}
        />
        <Button disabled={Boolean(fetching)} className="marginTop-small2 marginBottom-medium" theme={BUTTON_THEMES.PRIMARY} type="submit">
          {zipCodeCta}
        </Button>
      </Form>
    </Panel>
  );
};

export default ServiceZipCodeEntry;

/*
  Notes:
    * Reusing most of ZipCode component at src/components/Sku/ZipCodeEmailEntry/index.tsx
    * Sending hard coded 'testB' (used in ServicePage.js) is zip + email treatment
*/
